"use client";

import T from "@marginly/ui/components/typography";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useState } from "react";

import { getFarmTokenSymbol } from "@/app/util";
import Space from "@/components/common/space";
import { useWeb3Context } from "@/contexts/web3";
import { PositionType } from "@/contracts/transform/position";
import { usePoolCoeffs, usePoolData } from "@/hooks/position";
import { useApply } from "@/hooks/util";
import { UserPointsResponse } from "@/types/api";
import { Bonus } from "@/types/common";
import { daysDisplay } from "@/util/days-display";
import { formatCoin } from "@/util/format-coin";

import { formatPercent } from "../../util/formatters";
import { daysTillMaturity } from "../../util/get-day-difference";
import { badgeByBonus } from "../common/bonus/badge-by-bonus";
import { PoolProgress } from "../common/pool-progress";
import { Noop } from "../util";
import { FarmModal } from "./modals/modal";
import { FarmPositionModal } from "./modals/position-modal";
import {
  BonusSection,
  BottomSection,
  DaysCard,
  FarmBottom,
  FarmCard,
  FarmLabel,
  FarmLogo,
  MainSection,
  MarginlyPoints,
} from "./styled";

interface FarmProps {
  symbol?: string;
  chainName: string;
  poolAddress: `0x${string}`;
  apy: number;
  icon?: string;
  userPoints: UserPointsResponse | undefined;
  bonus?: Bonus[] | undefined;
  maturityDate?: string;
  totalDebt: number;
  totalCollateral: number;
}

export function FarmUnit({
  symbol,
  apy,
  icon,
  poolAddress,
  chainName,
  userPoints,
  maturityDate,
  totalCollateral,
  totalDebt,
}: FarmProps) {
  const [isFarmModalOpen, setFarmModalStatus] = useState(false);
  const [isFarmPositionModalOpen, setFarmPositionModalStatus] = useState(false);
  const pointDetails = poolAddress && userPoints?.detailsByPool[poolAddress];
  const poolCoeffs = usePoolCoeffs(poolAddress);
  const poolData = usePoolData(poolAddress);
  const positionParams = useApply(poolData.getPositionParams, poolCoeffs);
  const { getPoolByAddress } = useWeb3Context();

  const protocol = getPoolByAddress(poolAddress)?.pool.protocol || "";
  const { days } = daysTillMaturity(maturityDate);

  const router = useRouter();

  const handleClick = () => {
    if (!poolData.isTokenInfoLoaded) return;
    if (poolData.position?.type === PositionType.Lend) {
      router.push(`/${chainName}/lend-info`);
      return;
    }
    document.body.style.overflow = "hidden";
    if (poolData.position?.type === PositionType.Long) {
      setFarmPositionModalStatus(true);
      return;
    }
    setFarmModalStatus(true);
  };

  return (
    <>
      <FarmCard onClick={handleClick}>
        <MainSection>
          <T headerL>
            {formatPercent(apy / 100, {
              maximumFractionDigits: 1,
            })}
          </T>
          <Space height={12} heightMobile={12} />
          {days === null ? null : (
            <DaysCard>
              <Image width={16} height={16} src="/images/clock.svg" alt="" />
              {daysDisplay(days)}
            </DaysCard>
          )}

          <FarmBottom>
            <T body>{getFarmTokenSymbol(symbol) || ""}</T>
            <T caption secondary>
              {protocol}
            </T>
          </FarmBottom>

          <FarmLogo className="logo" src={icon} alt="" />
          <BonusSection>
            <MarginlyPoints>
              <Image
                src="/images/marginly-point-on-light.svg"
                alt=""
                width={24}
                height={24}
              />
              2&times;
            </MarginlyPoints>
          </BonusSection>
        </MainSection>

        <BottomSection>
          <PoolProgress
            collateral={totalCollateral}
            debt={totalDebt}
            tokenSymbol={symbol}
          />
        </BottomSection>
      </FarmCard>

      {isFarmModalOpen ? (
        <FarmModal
          symbol={symbol}
          chainName={chainName}
          poolAddress={poolAddress}
          maturityDate={maturityDate}
          days={days}
          onClose={() => {
            document.body.style.overflow = "auto";
            setFarmModalStatus(false);
          }}
        />
      ) : (
        <Noop />
      )}
      {isFarmPositionModalOpen ? (
        <FarmPositionModal
          poolAddress={poolAddress}
          maturityDate={maturityDate}
          chainName={chainName}
          onClose={() => {
            document.body.style.overflow = "auto";
            setFarmPositionModalStatus(false);
          }}
          points={pointDetails?.farmingPoints}
          pointsPerDay={pointDetails?.farmingPointsPerDay}
          leverage={positionParams?.leverage}
        />
      ) : (
        <Noop />
      )}
    </>
  );
}
